import type { Infer } from 'superstruct';
import {
	array,
	boolean,
	defaulted,
	dynamic,
	never,
	nullable,
	number,
	object,
	optional,
	partial,
	string,
	trimmed,
} from 'superstruct';

import {
	getFieldParentObject,
	parsableInteger,
	phoneNumber,
	stringDate,
	userString,
	withMessage,
} from '@adflow/shared/util-superstruct';

import {
	AddFacebookAdCreativeMediaTemplateDto,
	ViewFacebookAdCreativeMediaTemplateDto,
} from './facebook-adcreative-media-template.dto';

export const AddFacebookAdCreativeTemplateDto = object({
	id: optional(parsableInteger()),
	name: userString('validations:Required'),
	callToActionType: userString('validations:Required'),
	primaryText: userString('validations:Required'),
	redirectUrl: userString('validations:Required'),
	phoneNumber: dynamic((value, { branch }) => {
		const adSet = branch.at(-6);
		if (adSet.destinationType === 'PHONE_CALL') {
			return phoneNumber('validations:Required');
		}
		return trimmed(optional(nullable(string())));
	}),
	urlTags: trimmed(string()),
	pixel: defaulted(boolean(), false),
	mapCard: dynamic((value, { branch }) => {
		const adTemplate = branch.at(-4);
		const parent = getFieldParentObject(branch);
		if (
			value &&
			adTemplate.adType === 'CAROUSEL' &&
			parent.multiShareOptimized
		) {
			return withMessage(never(), 'validations:Map Card Incompatible');
		}
		return defaulted(boolean(), false);
	}),
	multiShareEndCard: defaulted(boolean(), false),
	multiShareOptimized: defaulted(boolean(), false),

	// Advantage+ creative enhancements
	advantagePlusAdaptToPlacement: defaulted(boolean(), false),
	advantagePlusAddTextOverlay: defaulted(boolean(), false),
	advantagePlusCreativeStickers: defaulted(boolean(), false),
	advantagePlusDescriptionAutomation: defaulted(boolean(), false),
	advantagePlusEnhanceCta: defaulted(boolean(), false),
	advantagePlusImageBackgroundGen: defaulted(boolean(), false),
	advantagePlusImageBrightnessContrast: defaulted(boolean(), false),
	advantagePlusImageTemplates: defaulted(boolean(), false),
	advantagePlusImageTouchups: defaulted(boolean(), false),
	advantagePlusImageUncrop: defaulted(boolean(), false),
	advantagePlusInlineComment: defaulted(boolean(), false),
	advantagePlusMediaTypeAutomation: defaulted(boolean(), false),
	advantagePlusPacRelaxation: defaulted(boolean(), false),
	advantagePlusProductExtensions: defaulted(boolean(), false),
	advantagePlusRevealDetailsOverTime: defaulted(boolean(), false),
	advantagePlusTextOptimizations: defaulted(boolean(), false),
	advantagePlusTextTranslation: defaulted(boolean(), false),
	advantagePlusVideoAutoCrop: defaulted(boolean(), false),

	adCreativeMediaTemplates: dynamic((value, { branch }) => {
		const ad = branch.at(-4);
		const adCreative = getFieldParentObject(branch);
		const mediaTemplates = value as
			AddFacebookAdCreativeMediaTemplateDto[] | undefined;

		const mapCard = !!adCreative.mapCard;
		const multiShareEndCard = !!adCreative.multiShareEndCard;

		if (mediaTemplates) {
			if (
				(ad.adType === 'IMAGE' || ad.adType === 'VIDEO') &&
				mediaTemplates?.length > 1
			) {
				return withMessage(never(), 'validations:Max Media Ad Type');
			} else if (
				ad.adType === 'CAROUSEL' &&
				mediaTemplates?.length > 10 - +mapCard - +multiShareEndCard
			) {
				return withMessage(never(), 'validations:Max Media Ad Type Carousel');
			}

			// Validate media file types match ad type
			if (ad.adType === 'VIDEO') {
				for (const template of mediaTemplates) {
					if (template.mediaFile) {
						const isInvalidImage =
							template.mediaFile.kind === 'IMAGE' &&
							!template.mediaFile.name.toLowerCase().endsWith('.gif');
						if (isInvalidImage) {
							return withMessage(never(), 'validations:Invalid Media Ad Type');
						}
					}
				}
			} else if (ad.adType === 'IMAGE') {
				for (const template of mediaTemplates) {
					if (template.mediaFile) {
						const isVideo = template.mediaFile.kind === 'VIDEO';
						const isGif = template.mediaFile.name
							.toLowerCase()
							.endsWith('.gif');
						if (isVideo || isGif) {
							return withMessage(never(), 'validations:Invalid Media Ad Type');
						}
					}
				}
			}
		}

		return optional(array(AddFacebookAdCreativeMediaTemplateDto));
	}),
});

export const ViewFacebookAdCreativeTemplateDto = object({
	id: number(),
	name: string(),
	callToActionType: string(),
	primaryText: string(),
	redirectUrl: optional(nullable(string())),
	phoneNumber: optional(nullable(string())),
	urlTags: string(),
	pixel: boolean(),
	mapCard: boolean(),
	multiShareEndCard: boolean(),
	multiShareOptimized: boolean(),

	// Advantage+ creative enhancements
	advantagePlusAdaptToPlacement: boolean(),
	advantagePlusAddTextOverlay: boolean(),
	advantagePlusCreativeStickers: boolean(),
	advantagePlusDescriptionAutomation: boolean(),
	advantagePlusEnhanceCta: boolean(),
	advantagePlusImageBackgroundGen: boolean(),
	advantagePlusImageBrightnessContrast: boolean(),
	advantagePlusImageTemplates: boolean(),
	advantagePlusImageTouchups: boolean(),
	advantagePlusImageUncrop: boolean(),
	advantagePlusInlineComment: boolean(),
	advantagePlusMediaTypeAutomation: boolean(),
	advantagePlusPacRelaxation: boolean(),
	advantagePlusProductExtensions: boolean(),
	advantagePlusRevealDetailsOverTime: boolean(),
	advantagePlusTextOptimizations: boolean(),
	advantagePlusTextTranslation: boolean(),
	advantagePlusVideoAutoCrop: boolean(),

	adCreativeMediaTemplates: optional(
		array(ViewFacebookAdCreativeMediaTemplateDto)
	),

	adCreatives: optional(
		array(
			object({
				id: number(),
				facebookId: string(),
				syncedAt: stringDate(),
			})
		)
	),
});
export type ViewFacebookAdCreativeTemplateDto = Infer<
	typeof ViewFacebookAdCreativeTemplateDto
>;
export type AddFacebookAdCreativeTemplateDto = Infer<
	typeof AddFacebookAdCreativeTemplateDto
>;
export const EditFacebookAdCreativeTemplateDto = partial(
	AddFacebookAdCreativeTemplateDto
);
export type EditFacebookAdCreativeTemplateDto = Infer<
	typeof EditFacebookAdCreativeTemplateDto
>;
